import clsx from 'clsx'
import React from 'react'
import { makeStyles } from '@material-ui/core'
import { useFormContext } from 'react-hook-form'
import SydButton from '../../../../../commonDesign/Button'
import SydLinkButton from '../../../../../commonDesign/LinkButton'
import { ADMIN_ROUTES } from '../../../../../../constants'
import { usePreviewDisplayContext } from '../PreviewDisplayContext'
import { useCheckPolicy } from '../../../../../../hooks'
import { InfographicBlock } from './InfographicBlock'
import { GridLines } from './gridLines'

const useStyles = makeStyles((theme) => ({
  currentPageContent: {
    flex: '0 0',
    border: 'var(--border-neutral-normal)',
    background: 'var(--color-neutral-300)',
    padding: '1rem',
    minWidth: 'fit-content',
    position: 'relative',
    zIndex: 100,
    borderRadius: 'var(--border-radius-sm)'
  },
  currentPageContentInner: {
    position: 'relative',
    width: '600px',
    height: '800px',
    margin: '0 auto'
  },
  gridDisplayContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridTemplateRows: 'repeat(16, 1fr)',
    display: 'grid',
    zIndex: 10,
    gap: '1px',
    borderRadius: 'var(--border-radius-sm)'
  },
  blockContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '0 1rem'
  },
  gridDisplayContentItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    border: 'var(--border-neutral-normal)',
    cursor: 'pointer',
    opacity: 1,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 'var(--border-radius-sm)',
    transitionDuration: '.125s',
    '&.not-selected': {
      opacity: 0.5
    },
    '&.selected': {
      opacity: 0.9,
      backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 25%, transparent)',
      borderColor: 'var(--border-color-primary-normal)'
    },
    '&.emptyBlock': {
      borderStyle: 'dashed'
    }
  },
  blockContentInner: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: '.25rem',
    lineHeight: '1em',
    overflow: 'hidden'
  },
  blockContentName: {
    width: '100%',
    lineHeight: '1.4em',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  deleteButton: {
    position: 'absolute',
    top: '5px',
    left: '5px',
    zIndex: '11'
  },
  editBlockButton: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    zIndex: '11'
  },
  hiddenInHtmlText: {
    position: 'absolute',
    zIndex: '11',
    bottom: '5px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'fit-content',
    textAlign: 'center',
    display: 'block',
    fontSize: '10px',
    lineHeight: '1em',
    backgroundColor: 'var(--color-neutral-300)',
    color: 'var(--text-color-main)',
    borderRadius: 'var(--border-radius-sm)',
    padding: '.3em 1em',
    margin: '0 auto'
  }
}))

export const TemplatePage = () => {
  const classes = useStyles()

  const userCanEditBlocks = useCheckPolicy('danger_zone')
  const { setValue, getValues } = useFormContext()
  const { currentPageContent, currentPageIndex, selectedBlockId, setSelectedBlockId } = usePreviewDisplayContext()

  const removeBlock = (block) => {
    const existingContent = getValues('content')
    const newContent = {
      ...existingContent,
      pages: existingContent.pages.map((page, index) => {
        if (index !== currentPageIndex) return page
        return {
          ...page,
          blocks: page.blocks.filter(b => b.key !== block.key)
        }
      })
    }
    setValue('content', newContent)
    if (selectedBlockId === block.key) {
      setSelectedBlockId(null)
    }
  }

  return (
    <div className={classes.currentPageContent}>
      <div className={classes.currentPageContentInner}>
        <GridLines cols={12} rows={16} />
        <div className={classes.gridDisplayContent}>
          {currentPageContent?.map((block) => {
            return (
              <div
                key={block.key}
                style={{
                  gridArea: `${block?.cords?.y + 1} / ${block?.cords?.x + 1} / span ${block.size?.rows} / span ${block.size?.cols}`
                }}
                className={clsx(
                  classes.gridDisplayContentItem,
                  selectedBlockId === block.key ? 'selected' : '',
                  selectedBlockId && selectedBlockId !== block.key ? 'not-selected' : '',
                  block.isEmptyBlock ? 'emptyBlock' : ''
                )}
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedBlockId(block.key)
                }}
              >
                {!block.isEmptyBlock ? (
                  <>
                    <SydButton
                      size='xs'
                      variant='primary'
                      priority='warning'
                      onClick={(e) => {
                        e.stopPropagation()
                        removeBlock(block)
                      }}
                      type='button'
                      className={classes.deleteButton}
                    >
                      Delete
                    </SydButton>

                    {userCanEditBlocks && (
                      <div className={classes.editBlockButton}>
                        <SydLinkButton
                          target='_blank'
                          size='xs'
                          variant='primary'
                          to={`${ADMIN_ROUTES.INFOGRAPHIC_REPORTS_BLOCK}/${block.infographicBlockId}`}
                          type='button'
                        >
                          Edit Block
                        </SydLinkButton>
                      </div>
                    )}

                    {block.hideInHtml && (
                      <div className={classes.hiddenInHtmlText}>(hidden in html)</div>
                    )}

                    <InfographicBlock
                      infographicBlock={block.infographicBlock}
                      containerSize={block.size}
                    />
                  </>
                ) : (
                  <div className='w-full h-full flex items-center justify-center'>
                    <p className='font-size-md p-md text-center'>
                      {selectedBlockId === block.key
                        ? 'Choose a block from the left to use'
                        : 'Click to add a block'}
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
